// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import { Role } from './ContextWrapper';
import { AgreementTypes } from '../Constants';

type RoleGroupType = (typeof AgreementTypes)[keyof typeof AgreementTypes][];

const roleObject = (name: string, heading: string, roleGroup: RoleGroupType, isChecked: boolean): Role => ({
    name,
    isChecked,
    roleGroup,
    heading,
});

export const roles = (isChecked: boolean = true): Role[] => {
    const allRoleGroup: RoleGroupType = [
        AgreementTypes.COMPANY,
        AgreementTypes.BROKER,
        AgreementTypes.ACCOUNTING,
        AgreementTypes.PROPERTYMGMT,
    ];
    return [
        // Statutory person insurances
        roleObject('insuranceInfoViewingRole', 'statutoryPersonHeading', allRoleGroup, isChecked),
        roleObject('insuranceInfoProcessingRole', 'statutoryPersonHeading', allRoleGroup, isChecked),
        roleObject('claimsReportingRole', 'statutoryPersonHeading', [AgreementTypes.COMPANY], isChecked),
        roleObject('claimsViewingRole', 'statutoryPersonHeading', [AgreementTypes.COMPANY], isChecked),
        roleObject('claimsInfoProcessingRole', 'statutoryPersonHeading', [AgreementTypes.COMPANY], isChecked),
        roleObject(
            'statisticViewingRole',
            'statutoryPersonHeading',
            [AgreementTypes.COMPANY, AgreementTypes.BROKER],
            isChecked
        ),
        roleObject('salaryInfoProcessingRole', 'statutoryPersonHeading', allRoleGroup, isChecked),
        // Group accident insurance roles
        roleObject('insuranceInfoViewingRole', 'groupAccidentHeading', allRoleGroup, isChecked),
        roleObject('insuranceInfoProcessingRole', 'groupAccidentHeading', allRoleGroup, isChecked),
        roleObject('claimsReportingRole', 'groupAccidentHeading', [AgreementTypes.COMPANY], isChecked),
        roleObject('claimsInfoProcessingRole', 'groupAccidentHeading', [AgreementTypes.COMPANY], isChecked),
        // Health care insurance roles
        roleObject('insuranceInfoViewingRole', 'healthCareHeading', allRoleGroup, isChecked),
        roleObject('insuranceInfoProcessingRole', 'healthCareHeading', allRoleGroup, isChecked),
        // Travel insurance roles
        roleObject('insuranceInfoViewingRole', 'travelHeading', allRoleGroup, isChecked),
        roleObject('insuranceInfoProcessingRole', 'travelHeading', allRoleGroup, isChecked),
        roleObject('claimsReportingRole', 'travelHeading', allRoleGroup, isChecked),
        roleObject('travelerClaimInformationProcessingRole', 'travelHeading', [AgreementTypes.COMPANY], isChecked),
        roleObject('luggageClaimInformationProcessingRole', 'travelHeading', allRoleGroup, isChecked),
        // Voluntary life, pension and disability insurance roles
        roleObject('insuranceInfoViewingRole', 'voluntaryLifePensionAndDisabilityHeading', allRoleGroup, isChecked),
        roleObject('insuranceInfoProcessingRole', 'voluntaryLifePensionAndDisabilityHeading', allRoleGroup, isChecked),
        // Motor vehicle insurance roles
        roleObject('insuranceInfoViewingRole', 'motorVehicleHeading', allRoleGroup, isChecked),
        roleObject('insuranceInfoProcessingRole', 'motorVehicleHeading', allRoleGroup, isChecked),
        roleObject('claimsReportingRole', 'motorVehicleHeading', allRoleGroup, isChecked),
        roleObject('claimsInfoProcessingRole', 'motorVehicleHeading', allRoleGroup, isChecked),
        // Property and liability insurance roles
        roleObject('insuranceInfoViewingRole', 'propertyAndLiabilityHeading', allRoleGroup, isChecked),
        roleObject('insuranceInfoProcessingRole', 'propertyAndLiabilityHeading', allRoleGroup, isChecked),
        roleObject('claimsReportingRole', 'propertyAndLiabilityHeading', allRoleGroup, isChecked),
        roleObject('propertyClaimInformationProcessingRole', 'propertyAndLiabilityHeading', allRoleGroup, isChecked),
        roleObject(
            'liabilityClaimInformationPersonProcessingRole',
            'propertyAndLiabilityHeading',
            [AgreementTypes.COMPANY],
            isChecked
        ),
        roleObject(
            'liabilityClaimInformationPropertyAndWealthProcessingRole',
            'propertyAndLiabilityHeading',
            allRoleGroup,
            isChecked
        ),
        roleObject(
            'interruptionClaimInformationPersonProcessingRole',
            'propertyAndLiabilityHeading',
            [AgreementTypes.COMPANY],
            isChecked
        ),
        roleObject(
            'interruptionClaimInformationPropertyAndLiabilityProcessingRole',
            'propertyAndLiabilityHeading',
            allRoleGroup,
            isChecked
        ),
        roleObject(
            'legalExpensesClaimInformationProcessingRole',
            'propertyAndLiabilityHeading',
            allRoleGroup,
            isChecked
        ),
    ];
};

export const roleHeading = [
    'statutoryPersonHeading',
    'groupAccidentHeading',
    'healthCareHeading',
    'travelHeading',
    'voluntaryLifePensionAndDisabilityHeading',
    'motorVehicleHeading',
    'propertyAndLiabilityHeading',
] as const;
